import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Building2, Users, FileText, MessageSquare, DollarSign, Shield,
  TrendingUp, AlertTriangle, Search, RefreshCw, MoreVertical,
  UserX, UserCheck, Key, ChevronRight, Activity, Cpu, Zap,
  BarChart3, Globe, Lock, CheckCircle2, XCircle, Crown
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_COLORS = {
  super_admin: "bg-purple-100 text-purple-800",
  admin:       "bg-blue-100 text-blue-800",
  avocat:      "bg-green-100 text-green-800",
  juriste:     "bg-teal-100 text-teal-800",
  relecteur:   "bg-amber-100 text-amber-800",
  stagiaire:   "bg-slate-100 text-slate-600",
};

const PIE_COLORS = ["#1e3a5f", "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

// ── Shared API helper ────────────────────────────────────────────────────────
function useApi() {
  const headers = () => {
    const token = localStorage.getItem("lawliet_token");
    return { Authorization: `Bearer ${token}` };
  };
  const get  = (url, params) => axios.get(`${API}${url}`,  { headers: headers(), params, withCredentials: true });
  const post = (url, data)   => axios.post(`${API}${url}`, data, { headers: headers(), withCredentials: true });
  return { get, post };
}

// ── KPI Card ─────────────────────────────────────────────────────────────────
function KPICard({ icon: Icon, label, value, sub, color = "blue", trend }) {
  const bg = {
    blue:   "bg-blue-50 text-blue-700",
    green:  "bg-green-50 text-green-700",
    amber:  "bg-amber-50 text-amber-700",
    purple: "bg-purple-50 text-purple-700",
    red:    "bg-red-50 text-red-700",
    teal:   "bg-teal-50 text-teal-700",
  }[color];

  return (
    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className={`p-2.5 rounded-xl ${bg}`}>
            <Icon className="h-5 w-5" />
          </div>
          {trend !== undefined && (
            <span className={`text-xs font-semibold ${trend >= 0 ? "text-green-600" : "text-red-500"}`}>
              {trend >= 0 ? "+" : ""}{trend}%
            </span>
          )}
        </div>
        <div className="mt-3">
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-sm font-medium text-slate-700 mt-0.5">{label}</p>
          {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

// ── DASHBOARD TAB ────────────────────────────────────────────────────────────
function DashboardTab({ data, loading }) {
  if (loading || !data) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-b-2 border-blue-900 rounded-full"/></div>;

  const { overview, growth, llm_cost, billing, security, roles_breakdown, charts } = data;

  const rolesData = Object.entries(roles_breakdown || {}).map(([role, count]) => ({ name: role, value: count }));

  return (
    <div className="space-y-6">
      {/* Primary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard icon={Building2} label="Cabinets Actifs"   value={overview.active_cabinets}  sub={`${overview.total_cabinets} total`}        color="blue"   />
        <KPICard icon={Users}     label="Utilisateurs"      value={overview.active_users}      sub={`${growth.new_users_this_week} cette semaine`} color="green"  />
        <KPICard icon={FileText}  label="Documents"         value={overview.total_documents}   sub={`${growth.new_docs_this_week} cette semaine`}  color="teal"   />
        <KPICard icon={MessageSquare} label="Messages IA"   value={overview.total_messages}    sub={`${growth.messages_today} aujourd'hui`}        color="purple" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard icon={DollarSign} label="MRR Estimé"        value={`${billing.mrr_euros} €`}     sub={`${billing.active_subs} abonnements actifs`} color="amber"  />
        <KPICard icon={Cpu}        label="Coût LLM Estimé"   value={`$${llm_cost.estimated_total_cost_usd}`} sub="Mistral Large (estimation)" color="red" />
        <KPICard icon={Lock}       label="MFA Activé"        value={`${security.mfa_adoption_rate}%`} sub={`${security.mfa_enabled_users} utilisateurs`} color="green" />
        <KPICard icon={AlertTriangle} label="Échecs Login 24h" value={security.failed_logins_24h} sub="Tentatives échouées" color={security.failed_logins_24h > 10 ? "red" : "teal"} />
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Daily signups */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              Inscriptions (30 derniers jours)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={charts.daily_signups || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{fontSize: 10}} tickFormatter={d => d?.slice(5)} />
                <YAxis tick={{fontSize: 10}} />
                <Tooltip labelFormatter={l => `Date: ${l}`} />
                <Line type="monotone" dataKey="count" stroke="#1e3a5f" strokeWidth={2} dot={false} name="Inscriptions" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Daily messages */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Activity className="h-4 w-4 text-purple-600" />
              Messages IA (30 derniers jours)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={charts.daily_messages || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{fontSize: 10}} tickFormatter={d => d?.slice(5)} />
                <YAxis tick={{fontSize: 10}} />
                <Tooltip labelFormatter={l => `Date: ${l}`} />
                <Bar dataKey="count" fill="#8b5cf6" radius={[3,3,0,0]} name="Messages" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Top cabinets */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-teal-600" />
              Top Cabinets (activité IA)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={charts.top_cabinets || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{fontSize: 10}} />
                <YAxis type="category" dataKey="name" tick={{fontSize: 10}} width={120} />
                <Tooltip />
                <Bar dataKey="messages" fill="#0d9488" radius={[0,3,3,0]} name="Messages" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Roles donut */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-600" />
              Répartition des Rôles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={rolesData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                  {rolesData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recent events */}
      {(data.recent_events || []).length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Activity className="h-4 w-4 text-slate-500" />
              Événements Récents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {data.recent_events.map((ev, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-slate-50 text-xs">
                  <span className="font-medium text-slate-700">{ev.action}</span>
                  <span className="text-slate-500">{ev.user_email}</span>
                  <span className={`font-semibold ${ev.status === "success" ? "text-green-600" : "text-red-500"}`}>{ev.status}</span>
                  <span className="text-slate-400">{ev.timestamp?.slice(0, 16)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── CABINETS TAB ─────────────────────────────────────────────────────────────
function CabinetsTab() {
  const { get, post } = useApi();
  const [cabinets, setCabinets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get("/superadmin/cabinets", { search: search || undefined });
      setCabinets(res.data.cabinets);
      setTotal(res.data.total);
    } catch { toast.error("Erreur chargement cabinets"); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (cabId, action) => {
    setActionLoading(cabId + action);
    try {
      await post(`/superadmin/cabinets/${cabId}/${action}`, {});
      toast.success(action === "suspend" ? "Cabinet suspendu" : "Cabinet réactivé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setActionLoading(null); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Rechercher un cabinet..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Button variant="outline" onClick={load} className="gap-2"><RefreshCw className="h-4 w-4" />Actualiser</Button>
      </div>
      <p className="text-sm text-slate-500">{total} cabinet{total > 1 ? "s" : ""} trouvé{total > 1 ? "s" : ""}</p>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin h-8 w-8 border-b-2 border-blue-900 rounded-full"/></div>
      ) : (
        <div className="space-y-3">
          {cabinets.map(cab => (
            <Card key={cab.id} className={`border-0 shadow-sm ${!cab.is_active ? "opacity-60" : ""}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  {/* Cabinet info */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`p-2 rounded-lg ${cab.is_active ? "bg-blue-100" : "bg-slate-100"}`}>
                      <Building2 className={`h-5 w-5 ${cab.is_active ? "text-blue-700" : "text-slate-400"}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-800 truncate">{cab.name}</p>
                        {!cab.is_active && <Badge variant="outline" className="text-red-600 border-red-200">Suspendu</Badge>}
                        {cab.subscription?.status === "trial" && <Badge className="bg-blue-100 text-blue-800">Essai</Badge>}
                        {cab.subscription?.status === "active" && <Badge className="bg-green-100 text-green-800">Actif</Badge>}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{cab.id}</p>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="hidden md:flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-lg font-bold text-slate-800">{cab.stats?.users || 0}</p>
                      <p className="text-xs text-slate-500">Utilisateurs</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-slate-800">{cab.stats?.documents || 0}</p>
                      <p className="text-xs text-slate-500">Documents</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-slate-800">{cab.stats?.messages || 0}</p>
                      <p className="text-xs text-slate-500">Messages IA</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {cab.is_active ? (
                      <Button
                        size="sm" variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        disabled={actionLoading === cab.id + "suspend"}
                        onClick={() => handleAction(cab.id, "suspend")}
                      >
                        <UserX className="h-3.5 w-3.5 mr-1.5" />
                        Suspendre
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="bg-green-700 hover:bg-green-800 text-white"
                        disabled={actionLoading === cab.id + "activate"}
                        onClick={() => handleAction(cab.id, "activate")}
                      >
                        <UserCheck className="h-3.5 w-3.5 mr-1.5" />
                        Réactiver
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {cabinets.length === 0 && (
            <div className="text-center py-16 text-slate-500">
              <Building2 className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>Aucun cabinet trouvé</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── USERS TAB ────────────────────────────────────────────────────────────────
function UsersTab() {
  const { get, post } = useApi();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get("/superadmin/users", {
        search: search || undefined,
        role: roleFilter || undefined,
        limit: 100,
      });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch { toast.error("Erreur chargement utilisateurs"); }
    finally { setLoading(false); }
  }, [search, roleFilter]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (userId, action, params = {}) => {
    setActionLoading(userId + action);
    try {
      await post(`/superadmin/users/${userId}/${action}`, params);
      toast.success(
        action === "suspend" ? "Utilisateur suspendu" :
        action === "activate" ? "Utilisateur réactivé" :
        action === "reset-mfa" ? "MFA réinitialisé" : "Action effectuée"
      );
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setActionLoading(null); }
  };

  const ROLES = ["", "super_admin", "admin", "avocat", "juriste", "relecteur", "stagiaire"];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Rechercher par email ou nom..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm text-slate-700 bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
        >
          {ROLES.map(r => <option key={r} value={r}>{r || "Tous les rôles"}</option>)}
        </select>
        <Button variant="outline" onClick={load} className="gap-2"><RefreshCw className="h-4 w-4" />Actualiser</Button>
      </div>
      <p className="text-sm text-slate-500">{total} utilisateur{total > 1 ? "s" : ""}</p>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin h-8 w-8 border-b-2 border-blue-900 rounded-full"/></div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                {["Utilisateur", "Cabinet", "Rôle", "MFA", "Messages", "Statut", "Actions"].map(h => (
                  <th key={h} className="text-left py-3 px-4 font-semibold text-slate-600 text-xs uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} className={`border-b border-slate-50 hover:bg-slate-50/50 ${!u.is_active ? "opacity-50" : ""}`}>
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium text-slate-800">{u.full_name}</p>
                      <p className="text-xs text-slate-500">{u.email}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded">{u.cabinet_name}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${ROLE_COLORS[u.role] || "bg-slate-100 text-slate-600"}`}>
                      {u.role === "super_admin" && <Crown className="h-2.5 w-2.5 inline mr-1" />}
                      {u.role}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {u.mfa_enabled
                      ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                      : <XCircle className="h-4 w-4 text-slate-300" />
                    }
                  </td>
                  <td className="py-3 px-4 text-slate-600">{u.messages_count ?? 0}</td>
                  <td className="py-3 px-4">
                    <Badge className={u.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                      {u.is_active ? "Actif" : "Suspendu"}
                    </Badge>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1">
                      {u.is_active ? (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:bg-red-50 text-xs"
                          disabled={actionLoading === u.id + "suspend"}
                          onClick={() => handleAction(u.id, "suspend")}
                        >Suspendre</Button>
                      ) : (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-green-700 hover:bg-green-50 text-xs"
                          disabled={actionLoading === u.id + "activate"}
                          onClick={() => handleAction(u.id, "activate")}
                        >Réactiver</Button>
                      )}
                      {u.mfa_enabled && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-amber-600 hover:bg-amber-50 text-xs"
                          disabled={actionLoading === u.id + "reset-mfa"}
                          onClick={() => handleAction(u.id, "reset-mfa")}
                        >Reset MFA</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && (
            <div className="text-center py-16 text-slate-500">
              <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>Aucun utilisateur trouvé</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── LLM ACTIVITY TAB ─────────────────────────────────────────────────────────
function LLMTab() {
  const { get } = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get("/superadmin/llm-activity", { days });
      setData(res.data);
    } catch { toast.error("Erreur chargement LLM"); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-8 w-8 border-b-2 border-blue-900 rounded-full"/></div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {[1, 7, 14, 30].map(d => (
            <Button key={d} size="sm" variant={days === d ? "default" : "outline"}
              className={days === d ? "bg-blue-600" : ""}
              onClick={() => setDays(d)}
            >{d} jour{d > 1 ? "s" : ""}</Button>
          ))}
        </div>
        <Button variant="outline" onClick={load} className="gap-2"><RefreshCw className="h-4 w-4" />Actualiser</Button>
      </div>

      {/* Cost summary */}
      <div className="grid grid-cols-3 gap-4">
        <KPICard icon={MessageSquare} label="Requêtes LLM" value={data?.total_messages || 0} color="purple" />
        <KPICard icon={DollarSign} label="Coût Estimé" value={`$${data?.total_estimated_cost_usd || 0}`} sub="Mistral Large input+output" color="amber" />
        <KPICard icon={Cpu} label="Modèle" value="Mistral Large" sub="mistral-large-latest" color="blue" />
      </div>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700">Activité Détaillée</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-y border-slate-100">
                <tr>
                  {["Horodatage", "Cabinet", "Utilisateur", "Tokens sortie (est.)", "Coût (est.)", "Modèle"].map(h => (
                    <th key={h} className="text-left py-2.5 px-4 font-semibold text-slate-600 text-xs uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.messages || []).slice(0, 50).map((m, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-2 px-4 text-slate-500 text-xs">{m.timestamp?.slice(0, 16)}</td>
                    <td className="py-2 px-4"><span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{m.cabinet}</span></td>
                    <td className="py-2 px-4 text-slate-600 text-xs">{m.user}</td>
                    <td className="py-2 px-4 text-slate-700">{m.est_output_tokens}</td>
                    <td className="py-2 px-4 font-mono text-xs text-amber-700">${m.est_cost_usd}</td>
                    <td className="py-2 px-4"><Badge variant="outline" className="text-xs">{m.model}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(data?.messages || []).length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Cpu className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p>Aucune activité LLM sur cette période</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── AUDIT LOG TAB ─────────────────────────────────────────────────────────────
function AuditTab() {
  const { get } = useApi();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get("/superadmin/audit-log", { action: actionFilter || undefined, limit: 100 });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch { toast.error("Erreur chargement audit"); }
    finally { setLoading(false); }
  }, [actionFilter]);

  useEffect(() => { load(); }, [load]);

  const STATUS_COLOR = { success: "text-green-600", failed: "text-red-500", pending: "text-amber-600" };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Input placeholder="Filtrer par action..." value={actionFilter} onChange={e => setActionFilter(e.target.value)} className="max-w-xs" />
        <Button variant="outline" onClick={load} className="gap-2"><RefreshCw className="h-4 w-4" />Actualiser</Button>
        <span className="text-sm text-slate-500 ml-auto">{total} événements</span>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin h-8 w-8 border-b-2 border-blue-900 rounded-full"/></div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                {["Horodatage", "Utilisateur", "Action", "Ressource", "Statut", "Détails"].map(h => (
                  <th key={h} className="text-left py-3 px-4 font-semibold text-slate-600 text-xs uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map((l, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="py-2.5 px-4 text-xs text-slate-400">{l.timestamp?.slice(0, 16)}</td>
                  <td className="py-2.5 px-4 text-xs text-slate-700">{l.user_email}</td>
                  <td className="py-2.5 px-4"><span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{l.action}</span></td>
                  <td className="py-2.5 px-4 text-xs text-slate-500">{l.resource_type}{l.resource_id ? ` (${l.resource_id?.slice(0,8)}…)` : ""}</td>
                  <td className={`py-2.5 px-4 text-xs font-semibold ${STATUS_COLOR[l.status] || "text-slate-600"}`}>{l.status}</td>
                  <td className="py-2.5 px-4 text-xs text-slate-500 max-w-xs truncate">{JSON.stringify(l.details || {})}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {logs.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Shield className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p>Aucun événement d'audit trouvé</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function SuperAdmin({ user, onLogout }) {
  const { get } = useApi();
  const [dashData, setDashData] = useState(null);
  const [dashLoading, setDashLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const res = await get("/superadmin/dashboard");
      setDashData(res.data);
      setLastRefresh(new Date());
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Accès refusé — Super Admin uniquement");
      } else {
        toast.error("Erreur chargement dashboard");
      }
    } finally { setDashLoading(false); }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-700 to-blue-900 rounded-lg flex items-center justify-center">
              <Crown className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Lawliet.ai — Super Admin</h1>
              <p className="text-xs text-slate-500">Panel d'administration de la plateforme</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {lastRefresh && (
              <span className="text-xs text-slate-400 hidden md:block">
                Actualisé à {lastRefresh.toLocaleTimeString("fr-FR")}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={loadDashboard} className="gap-2">
              <RefreshCw className="h-3.5 w-3.5" />
              Actualiser
            </Button>
            <div className="flex items-center gap-2 pl-3 border-l border-slate-200">
              <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold text-purple-800">{user?.full_name?.[0] || "A"}</span>
              </div>
              <div className="hidden md:block">
                <p className="text-xs font-semibold text-slate-700">{user?.full_name}</p>
                <p className="text-xs text-purple-600 font-medium">Super Admin</p>
              </div>
              <Button variant="ghost" size="sm" onClick={onLogout} className="text-xs text-slate-500 hover:text-red-600 ml-2">
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-[1400px] mx-auto px-6 py-6">
        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 shadow-sm p-1 h-auto gap-1">
            {[
              { value: "dashboard", icon: BarChart3,    label: "Dashboard" },
              { value: "cabinets",  icon: Building2,    label: "Cabinets" },
              { value: "users",     icon: Users,        label: "Utilisateurs" },
              { value: "llm",       icon: Cpu,          label: "Activité LLM" },
              { value: "audit",     icon: Shield,       label: "Audit Log" },
            ].map(tab => (
              <TabsTrigger key={tab.value} value={tab.value}
                className="gap-2 px-4 py-2.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-md transition-all"
              >
                <tab.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="dashboard">
            <DashboardTab data={dashData} loading={dashLoading} />
          </TabsContent>
          <TabsContent value="cabinets">
            <CabinetsTab />
          </TabsContent>
          <TabsContent value="users">
            <UsersTab />
          </TabsContent>
          <TabsContent value="llm">
            <LLMTab />
          </TabsContent>
          <TabsContent value="audit">
            <AuditTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
